import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete, Switch, FormControlLabel, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createRecurringPaymentSchedule, updateRecurringPaymentSchedule, listCashAccounts,
  type RecurringPaymentSchedule, type SaveRecurringPaymentSchedulePayload,
} from '../../api/financiar'
import { listSuppliers } from '../../api/furnizori'
import { errorMessage } from '../../api/client'

const schema = z.object({
  description: z.string().min(1, 'Câmp obligatoriu'),
  supplierId: z.string().nullable(),
  amount: z.string().min(1, 'Câmp obligatoriu'),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  cashAccountId: z.string().min(1, 'Selectați un cont'),
  category: z.string().nullable(),
  intervalType: z.enum(['Lunar', 'Trimestrial', 'Semestrial', 'Anual']),
  nextDueDate: z.string().min(1, 'Câmp obligatoriu'),
  isActive: z.boolean(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  description: '', supplierId: null, amount: '', currency: 'RON', cashAccountId: '', category: '',
  intervalType: 'Lunar', nextDueDate: new Date().toISOString().slice(0, 10), isActive: true,
}

export default function RecurringScheduleFormDialog({ target, onClose }: { target: RecurringPaymentSchedule | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts, enabled: open })
  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ defaultValues: EMPTY, resolver: zodResolver(schema) })

  useEffect(() => {
    if (editing) {
      const t = target as RecurringPaymentSchedule
      reset({
        description: t.description, supplierId: t.supplierId, amount: String(t.amount), currency: t.currency,
        cashAccountId: t.cashAccountId, category: t.category, intervalType: t.intervalType, nextDueDate: t.nextDueDate, isActive: t.isActive,
      })
    } else if (target === 'new') reset(EMPTY)
  }, [target, editing, reset])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: SaveRecurringPaymentSchedulePayload = { ...values, amount: Number(values.amount) }
      return editing ? updateRecurringPaymentSchedule((target as RecurringPaymentSchedule).id, payload).then(() => undefined) : createRecurringPaymentSchedule(payload)
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['financiar', 'programari-plati'] }); onClose() },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare programare' : 'Programare de plată recurentă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="description" control={control} render={({ field }) => (
              <TextField {...field} label="Descriere" fullWidth error={!!errors.description} helperText={errors.description?.message} />
            )} />
            <Controller name="supplierId" control={control} render={({ field }) => (
              <Autocomplete
                options={suppliers?.map((s) => s.id) ?? []}
                getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
                value={field.value} onChange={(_, v) => field.onChange(v)}
                renderInput={(params) => <TextField {...params} label="Furnizor (opțional)" />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="amount" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Sumă" sx={{ width: 140 }} error={!!errors.amount} helperText={errors.amount?.message} />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 110 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
              <Controller name="category" control={control} render={({ field }) => (
                <TextField {...field} value={field.value ?? ''} label="Categorie" fullWidth />
              )} />
            </Stack>
            <Controller name="cashAccountId" control={control} render={({ field }) => (
              <TextField {...field} select label="Cont" error={!!errors.cashAccountId} helperText={errors.cashAccountId?.message}>
                {accounts?.map((a) => <MenuItem key={a.id} value={a.id}>{a.name} ({a.currency})</MenuItem>)}
              </TextField>
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="intervalType" control={control} render={({ field }) => (
                <TextField {...field} select label="Interval" sx={{ width: 160 }}>
                  <MenuItem value="Lunar">Lunar</MenuItem>
                  <MenuItem value="Trimestrial">Trimestrial</MenuItem>
                  <MenuItem value="Semestrial">Semestrial</MenuItem>
                  <MenuItem value="Anual">Anual</MenuItem>
                </TextField>
              )} />
              <Controller name="nextDueDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Următoarea scadență" sx={{ width: 190 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.nextDueDate} helperText={errors.nextDueDate?.message} />
              )} />
            </Stack>
            <Controller name="isActive" control={control} render={({ field }) => (
              <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Activă" />
            )} />
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
